import { useAuth } from './useAuth';
import type { UserRole } from '../../shared/types';
import { jwtDecode } from 'jwt-decode';

interface DecodedToken {
  'http://schemas.microsoft.com/ws/2008/06/identity/claims/role'?: string | string[];
  permission?: string | string[];
}

export function usePermissions() {
  const { user, token } = useAuth();

  // Extrai roles do token JWT (fonte principal de verdade)
  const getRolesFromToken = (): string[] => {
    if (!token) return [];
    
    try {
      const decoded = jwtDecode<DecodedToken>(token);
      const rolesClaim = decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      
      if (Array.isArray(rolesClaim)) {
        return rolesClaim;
      } else if (rolesClaim) {
        return [rolesClaim];
      }
      return [];
    } catch (error) {
      console.error('Erro ao decodificar token:', error);
      return [];
    }
  };

  // Extrai permissões do token JWT
  const getPermissionsFromToken = (): string[] => {
    if (!token) return [];
    
    try {
      const decoded = jwtDecode<DecodedToken>(token);
      const permissionsClaim = decoded.permission;
      
      if (Array.isArray(permissionsClaim)) {
        return permissionsClaim;
      } else if (permissionsClaim) {
        return [permissionsClaim];
      }
      return [];
    } catch (error) {
      console.error('Erro ao decodificar token:', error);
      return [];
    }
  };

  const rolesFromToken = getRolesFromToken();
  const permissionsFromToken = getPermissionsFromToken();

  const hasRole = (role: UserRole): boolean => {
    // Normaliza para comparação case-insensitive
    const roleLower = role.toLowerCase();
    return rolesFromToken.some(r => r.toLowerCase() === roleLower) || 
           user?.roles?.some(r => r.toLowerCase() === roleLower) || 
           false;
  };

  const hasAnyRole = (roles: UserRole[]): boolean => {
    return roles.some(role => hasRole(role));
  };

  const hasAllRoles = (roles: UserRole[]): boolean => {
    return roles.every(role => hasRole(role));
  };

  const hasPermission = (permission: string): boolean => {
    return permissionsFromToken.includes(permission);
  };

  const hasAnyPermission = (permissions: string[]): boolean => {
    return permissions.some(p => hasPermission(p));
  };

  const isAdmin = hasRole('admin');
  const isInstrutor = hasRole('instrutor');
  const isAluno = hasRole('aluno');

  const canManageUsers = isAdmin || hasPermission('users.manage');
  const canManageCursos = isAdmin || isInstrutor || hasPermission('courses.manage');
  const canManageTrilhas = isAdmin || hasPermission('trilhas.manage');
  const canViewAdminPanel = isAdmin;

  return {
    user,
    roles: rolesFromToken,
    permissions: permissionsFromToken,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    hasPermission,
    hasAnyPermission,
    isAdmin,
    isInstrutor,
    isAluno,
    canManageUsers,
    canManageCursos,
    canManageTrilhas,
    canViewAdminPanel,
  };
}
